import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../../UserContext';
import {
    ChevronLeft,
    Users,
    Search,
    UserPlus,
    Shield,
    Crown,
    User,
    MoreVertical,
    Trash2,
    Edit3,
    X,
    Loader2,
    Check,
    AlertTriangle,
    Mail,
    Clock3
} from 'lucide-react';
import { clubsService } from '../../../services';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';

const getRoleBadge = (level) => {
    switch (level) {
        case 2:
            return { label: 'Owner', icon: Crown, color: 'gold' };
        case 1:
            return { label: 'Admin', icon: Shield, color: 'blue' };
        default:
            return { label: 'Member', icon: User, color: 'gray' };
    }
};

const getDisplayName = (member) => {
    const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
    if (fullName) {
        return fullName;
    }

    return member.userName ? `@${member.userName}` : member.email || 'Member';
};

const getInitials = (member) => {
    const first = member.firstName?.charAt(0) || member.userName?.charAt(0) || member.email?.charAt(0) || 'M';
    const last = member.lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
};

const ManageMembers = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [members, setMembers] = useState([]);
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [club, setClub] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [actionMenuOpen, setActionMenuOpen] = useState(null);
    const [roleFilter, setRoleFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name_asc');
    const [roleAuditEntries, setRoleAuditEntries] = useState([]);
    const [activeMemberActionId, setActiveMemberActionId] = useState(null);
    const [activeInvitationId, setActiveInvitationId] = useState(null);
    const [inviteIdentifier, setInviteIdentifier] = useState('');
    const [newMemberLevel, setNewMemberLevel] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchClubDetails = useCallback(async () => {
        try {
            const data = await clubsService.getClub(clubId);
            setClub(data);
        } catch (err) {
            console.error('Failed to fetch club details:', err);
        }
    }, [clubId]);

    const fetchMembers = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await clubsService.getClubMembers(clubId);
            setMembers(data?.members || []);
            setPendingInvitations(data?.pendingInvitations || []);
        } catch (err) {
            console.error(err);
            setError('Failed to load members. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        fetchClubDetails();
        fetchMembers();
    }, [fetchClubDetails, fetchMembers]);

    const handleInviteMember = async () => {
        if (!inviteIdentifier.trim()) return;

        setIsSubmitting(true);
        setError(null);

        try {
            await clubsService.inviteClubMember(clubId, {
                identifier: inviteIdentifier.trim(),
                userLevel: newMemberLevel
            });

            await fetchMembers();
            setRoleAuditEntries((prev) => [
                {
                    id: Date.now(),
                    action: 'Invitation sent',
                    detail: `${inviteIdentifier.trim()} invited as ${getRoleBadge(newMemberLevel).label}`,
                    at: new Date().toISOString()
                },
                ...prev
            ]);
            setShowAddModal(false);
            setInviteIdentifier('');
            setNewMemberLevel(0);
        } catch (err) {
            console.error('Failed to invite member:', err);
            setError(err.message || 'Failed to send invitation. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelInvitation = async (invitation) => {
        if (!window.confirm(`Cancel the pending invitation for ${invitation.maskedDestination}?`)) {
            return;
        }

        setActiveInvitationId(invitation.id);
        setError(null);

        try {
            await clubsService.cancelClubInvitation(clubId, invitation.id);
            await fetchMembers();
            setRoleAuditEntries((prev) => [
                {
                    id: Date.now(),
                    action: 'Invitation cancelled',
                    detail: `${invitation.maskedDestination} invitation cancelled`,
                    at: new Date().toISOString()
                },
                ...prev
            ]);
        } catch (err) {
            console.error('Failed to cancel invitation:', err);
            setError(err.message || 'Failed to cancel invitation.');
        } finally {
            setActiveInvitationId(null);
        }
    };

    const handleUpdateRole = async () => {
        if (!selectedMember) return;

        const oldRole = getRoleBadge(selectedMember.userLevel).label;
        const newRole = getRoleBadge(selectedMember.newLevel).label;
        const memberName = getDisplayName(selectedMember);

        if (selectedMember.userLevel !== selectedMember.newLevel) {
            const isOwnerChange = selectedMember.userLevel === 2 || selectedMember.newLevel === 2;
            const warning = isOwnerChange
                ? `Owner-level change: ${memberName} will move from ${oldRole} to ${newRole}. Continue?`
                : `Change role from ${oldRole} to ${newRole} for ${memberName}?`;

            if (!window.confirm(warning)) {
                return;
            }
        }

        setIsSubmitting(true);
        try {
            await clubsService.updateClubMemberRole(clubId, selectedMember.id, {
                userLevel: selectedMember.newLevel
            });
            await fetchMembers();
            setRoleAuditEntries((prev) => [
                {
                    id: Date.now(),
                    action: 'Role changed',
                    detail: `${memberName}: ${oldRole} to ${newRole}`,
                    at: new Date().toISOString()
                },
                ...prev
            ]);
            setShowEditModal(false);
            setSelectedMember(null);
        } catch (err) {
            console.error('Failed to update role:', err);
            setError(err.message || 'Failed to update role. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveMember = async (member) => {
        const role = getRoleBadge(member.userLevel).label;
        const memberName = getDisplayName(member);
        const warning = member.userLevel === 2
            ? `Owner removal: ${memberName} is an Owner. Remove from this venue?`
            : `Are you sure you want to remove ${memberName} from this venue?`;

        if (!window.confirm(warning)) {
            return;
        }

        setActiveMemberActionId(member.id);
        try {
            await clubsService.removeClubMember(clubId, member.id);
            await fetchMembers();
            setRoleAuditEntries((prev) => [
                {
                    id: Date.now(),
                    action: 'Member removed',
                    detail: `${memberName} removed (${role})`,
                    at: new Date().toISOString()
                },
                ...prev
            ]);
        } catch (err) {
            console.error('Failed to remove member:', err);
            setError(err.message || 'Failed to remove member. Please try again.');
        } finally {
            setActiveMemberActionId(null);
        }
        setActionMenuOpen(null);
    };

    const currentUserId = Number(user?.userId || user?.id || 0);
    const currentMemberRecord = members.find((member) => member.userId === currentUserId);
    const currentUserLevel = user?.isAdmin ? 99 : Number(currentMemberRecord?.userLevel ?? -1);
    const canManageMembers = currentUserLevel >= 1;
    const canAssignOwnerOrAdmin = currentUserLevel >= 2;

    const canEditMember = (member) => {
        if (user?.isAdmin) return true;
        if (!canManageMembers) return false;
        if (member.userLevel >= 1 && !canAssignOwnerOrAdmin) return false;
        return true;
    };

    const canRemoveMember = (member) => {
        if (user?.isAdmin) return true;
        if (!canManageMembers) return false;
        if (member.userLevel >= 1 && !canAssignOwnerOrAdmin) return false;
        return true;
    };

    const canChooseRoleLevel = (level) => {
        if (user?.isAdmin) return true;
        if (!canManageMembers) return false;
        if (level >= 1) return canAssignOwnerOrAdmin;
        return true;
    };

    const memberCounts = {
        owners: members.filter((member) => member.userLevel === 2).length,
        admins: members.filter((member) => member.userLevel === 1).length,
        members: members.filter((member) => member.userLevel === 0).length
    };

    const filteredMembers = members
        .filter((member) => {
            const normalizedSearch = searchTerm.toLowerCase();
            const matchesSearch =
                getDisplayName(member).toLowerCase().includes(normalizedSearch) ||
                member.email?.toLowerCase().includes(normalizedSearch) ||
                member.userName?.toLowerCase().includes(normalizedSearch);

            if (!matchesSearch) {
                return false;
            }

            if (roleFilter === 'all') {
                return true;
            }

            return Number(roleFilter) === member.userLevel;
        })
        .sort((a, b) => {
            if (sortBy === 'role_desc') {
                return b.userLevel - a.userLevel;
            }

            if (sortBy === 'role_asc') {
                return a.userLevel - b.userLevel;
            }

            const nameA = getDisplayName(a).toLowerCase();
            const nameB = getDisplayName(b).toLowerCase();
            return sortBy === 'name_desc' ? nameB.localeCompare(nameA) : nameA.localeCompare(nameB);
        });

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading members...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            <button
                className="club-dashboard-back"
                onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}
            >
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-blue">
                        <Users size={24} />
                    </div>
                    <div>
                        <h1>Manage Members</h1>
                        <p>{members.length} member{members.length !== 1 ? 's' : ''} at {club?.name}</p>
                    </div>
                </div>
                <button
                    className="manage-section-add-btn"
                    onClick={() => setShowAddModal(true)}
                    disabled={!canManageMembers}
                >
                    <UserPlus size={18} />
                    Invite Member
                </button>
            </div>

            {error && (
                <div className="manage-clubs-error">
                    {error}
                    <button onClick={() => setError(null)} className="error-dismiss">
                        <X size={16} />
                    </button>
                </div>
            )}

            <div className="manage-section-toolbar">
                <div className="manage-clubs-search">
                    <Search size={18} className="manage-clubs-search-icon" />
                    <input
                        type="text"
                        placeholder="Search members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="manage-clubs-sort">
                    <Shield size={16} />
                    <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                        <option value="all">All roles</option>
                        <option value="2">Owners</option>
                        <option value="1">Admins</option>
                        <option value="0">Members</option>
                    </select>
                </div>
                <div className="manage-clubs-sort">
                    <Users size={16} />
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="name_asc">Name A-Z</option>
                        <option value="name_desc">Name Z-A</option>
                        <option value="role_desc">Role High-Low</option>
                        <option value="role_asc">Role Low-High</option>
                    </select>
                </div>
            </div>

            <div className="member-role-summary">
                <span className="member-role-chip role-gold"><Crown size={14} /> {memberCounts.owners} owner{memberCounts.owners !== 1 ? 's' : ''}</span>
                <span className="member-role-chip role-blue"><Shield size={14} /> {memberCounts.admins} admin{memberCounts.admins !== 1 ? 's' : ''}</span>
                <span className="member-role-chip role-gray"><User size={14} /> {memberCounts.members} member{memberCounts.members !== 1 ? 's' : ''}</span>
            </div>

            <div className="member-permissions-helper">
                <div className="member-permissions-helper-header">
                    <AlertTriangle size={18} />
                    <h3>Privacy-first member flow</h3>
                </div>
                <p>
                    Invite by exact username or email only. We do not surface searchable account results here, so venue teams cannot browse member details before the user accepts.
                </p>
                <p>
                    Your access: {user?.isAdmin ? 'Platform Admin' : getRoleBadge(currentUserLevel).label}.
                    {canAssignOwnerOrAdmin ? ' You can assign all roles.' : ' You can only manage member-level access.'}
                </p>
                <p>Owners can assign all roles and manage owner/admin access. Admins can manage member-level access and day-to-day member operations.</p>
            </div>

            {pendingInvitations.length > 0 && (
                <div className="pending-invitations-panel">
                    <div className="pending-invitations-header">
                        <h3>Pending Invitations</h3>
                        <span>{pendingInvitations.length} waiting</span>
                    </div>
                    <div className="pending-invitations-list">
                        {pendingInvitations.map((invitation) => {
                            const role = getRoleBadge(invitation.userLevel);
                            const RoleIcon = role.icon;
                            return (
                                <div key={invitation.id} className="pending-invitation-card">
                                    <div className="pending-invitation-main">
                                        <div className="pending-invitation-icon">
                                            <Mail size={18} />
                                        </div>
                                        <div>
                                            <h4>{invitation.maskedDestination}</h4>
                                            <p>
                                                {invitation.isExistingUserInvite
                                                    ? 'Existing Shoot Connect user invited privately'
                                                    : 'Invite email sent to join Shoot Connect and accept membership'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className={`member-role-badge role-${role.color}`}>
                                        <RoleIcon size={14} />
                                        {role.label}
                                    </div>
                                    <div className="pending-invitation-time">
                                        <Clock3 size={14} />
                                        {new Date(invitation.createdAt).toLocaleDateString('en-GB')}
                                    </div>
                                    <button
                                        className="member-action-inline danger"
                                        disabled={activeInvitationId === invitation.id}
                                        onClick={() => handleCancelInvitation(invitation)}
                                    >
                                        {activeInvitationId === invitation.id ? <Loader2 size={16} className="btn-spinner" /> : <Trash2 size={16} />}
                                        Cancel
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {filteredMembers.length === 0 ? (
                <div className="manage-clubs-empty">
                    <div className="manage-clubs-empty-icon">
                        <Users size={48} />
                    </div>
                    <h3>No Members Found</h3>
                    <p>{searchTerm ? 'Try a different search term' : 'Invite your first member to get started'}</p>
                </div>
            ) : (
                <div className="members-list">
                    {filteredMembers.map((member) => {
                        const role = getRoleBadge(member.userLevel);
                        const RoleIcon = role.icon;
                        return (
                            <div key={member.id} className="member-card">
                                <div className="member-avatar">
                                    {getInitials(member)}
                                </div>
                                <div className="member-info">
                                    <h4>{getDisplayName(member)}</h4>
                                    <p>{member.email}</p>
                                    {member.userName && <span className="member-username">@{member.userName}</span>}
                                </div>
                                <div className={`member-role-badge role-${role.color}`}>
                                    <RoleIcon size={14} />
                                    {role.label}
                                </div>
                                <div className="member-actions">
                                    <button
                                        className="member-action-btn"
                                        onClick={() => setActionMenuOpen(actionMenuOpen === member.id ? null : member.id)}
                                    >
                                        <MoreVertical size={18} />
                                    </button>
                                    {actionMenuOpen === member.id && (
                                        <div className="member-action-menu">
                                            <button
                                                disabled={!canEditMember(member)}
                                                title={!canEditMember(member) ? 'Only owners can change admin or owner roles.' : ''}
                                                onClick={() => {
                                                    if (!canEditMember(member)) return;
                                                    setSelectedMember({ ...member, newLevel: member.userLevel });
                                                    setShowEditModal(true);
                                                    setActionMenuOpen(null);
                                                }}
                                            >
                                                <Edit3 size={16} />
                                                Change Role
                                            </button>
                                            <button
                                                className="danger"
                                                disabled={activeMemberActionId === member.id || !canRemoveMember(member)}
                                                title={!canRemoveMember(member) ? 'Only owners can remove admin or owner accounts.' : ''}
                                                onClick={() => handleRemoveMember(member)}
                                            >
                                                <Trash2 size={16} />
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {roleAuditEntries.length > 0 && (
                <div className="member-audit-panel">
                    <h3>Recent Role Activity</h3>
                    <div className="member-audit-list">
                        {roleAuditEntries.slice(0, 8).map((entry) => (
                            <div key={entry.id} className="member-audit-item">
                                <strong>{entry.action}</strong>
                                <span>{entry.detail}</span>
                                <time>{new Date(entry.at).toLocaleString()}</time>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Invite Member</h2>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Username or email</label>
                                <div className="search-input-wrapper">
                                    <Mail size={18} className="search-input-icon" />
                                    <input
                                        type="text"
                                        placeholder="Enter exact username or email"
                                        value={inviteIdentifier}
                                        onChange={(e) => setInviteIdentifier(e.target.value)}
                                    />
                                </div>
                                <p className="form-helper-text">
                                    Existing Shoot Connect users can be invited by username or email. To invite someone new, use their email address so we can send them a join-and-accept link.
                                </p>
                            </div>

                            <div className="invite-privacy-note">
                                <AlertTriangle size={16} />
                                <span>Search suggestions are hidden here on purpose to avoid exposing user details before membership is accepted.</span>
                            </div>

                            <div className="form-group">
                                <label>Role</label>
                                <div className="role-options">
                                    <button
                                        type="button"
                                        className={`role-option ${newMemberLevel === 0 ? 'selected' : ''}`}
                                        onClick={() => setNewMemberLevel(0)}
                                    >
                                        <User size={18} />
                                        <span>Member</span>
                                        {newMemberLevel === 0 && <Check size={16} className="role-check" />}
                                    </button>
                                    <button
                                        type="button"
                                        className={`role-option ${newMemberLevel === 1 ? 'selected' : ''}`}
                                        disabled={!canChooseRoleLevel(1)}
                                        onClick={() => setNewMemberLevel(1)}
                                    >
                                        <Shield size={18} />
                                        <span>Admin</span>
                                        {newMemberLevel === 1 && <Check size={16} className="role-check" />}
                                    </button>
                                    <button
                                        type="button"
                                        className={`role-option ${newMemberLevel === 2 ? 'selected' : ''}`}
                                        disabled={!canChooseRoleLevel(2)}
                                        onClick={() => setNewMemberLevel(2)}
                                    >
                                        <Crown size={18} />
                                        <span>Owner</span>
                                        {newMemberLevel === 2 && <Check size={16} className="role-check" />}
                                    </button>
                                </div>
                                {newMemberLevel === 2 && (
                                    <p className="role-warning-text">
                                        Owner role gives full venue control, including other role assignments.
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn-secondary"
                                onClick={() => setShowAddModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleInviteMember}
                                disabled={!inviteIdentifier.trim() || isSubmitting}
                            >
                                {isSubmitting ? <Loader2 size={18} className="btn-spinner" /> : null}
                                Send Invitation
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && selectedMember && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Change Role</h2>
                            <button className="modal-close" onClick={() => setShowEditModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="selected-user-card" style={{ marginBottom: '20px' }}>
                                <div className="selected-user-info">
                                    <div className="user-search-avatar">
                                        {getInitials(selectedMember)}
                                    </div>
                                    <div>
                                        <span className="user-search-name">{getDisplayName(selectedMember)}</span>
                                        <span className="user-search-email">{selectedMember.email}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>New Role</label>
                                <div className="role-options">
                                    <button
                                        type="button"
                                        className={`role-option ${selectedMember.newLevel === 0 ? 'selected' : ''}`}
                                        disabled={!canChooseRoleLevel(0)}
                                        onClick={() => setSelectedMember({ ...selectedMember, newLevel: 0 })}
                                    >
                                        <User size={18} />
                                        <span>Member</span>
                                        {selectedMember.newLevel === 0 && <Check size={16} className="role-check" />}
                                    </button>
                                    <button
                                        type="button"
                                        className={`role-option ${selectedMember.newLevel === 1 ? 'selected' : ''}`}
                                        disabled={!canChooseRoleLevel(1)}
                                        onClick={() => setSelectedMember({ ...selectedMember, newLevel: 1 })}
                                    >
                                        <Shield size={18} />
                                        <span>Admin</span>
                                        {selectedMember.newLevel === 1 && <Check size={16} className="role-check" />}
                                    </button>
                                    <button
                                        type="button"
                                        className={`role-option ${selectedMember.newLevel === 2 ? 'selected' : ''}`}
                                        disabled={!canChooseRoleLevel(2)}
                                        onClick={() => setSelectedMember({ ...selectedMember, newLevel: 2 })}
                                    >
                                        <Crown size={18} />
                                        <span>Owner</span>
                                        {selectedMember.newLevel === 2 && <Check size={16} className="role-check" />}
                                    </button>
                                </div>
                                {(selectedMember.newLevel === 2 || selectedMember.userLevel === 2) && (
                                    <p className="role-warning-text">
                                        Owner-level changes are high impact and should be used carefully.
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn-secondary"
                                onClick={() => setShowEditModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleUpdateRole}
                                disabled={selectedMember.newLevel === selectedMember.userLevel || isSubmitting}
                            >
                                {isSubmitting ? <Loader2 size={18} className="btn-spinner" /> : null}
                                Update Role
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageMembers;
